import type { Router } from 'vue-router';

import { applyDarkMode } from 'src/composables/useDarkMode';
import { MODULE_MAP } from 'src/composables/useSearchExecution';
import { attachShortcutListener, useShortcutRegistry } from 'src/composables/useShortcutRegistry';
import { useSpotlightSearch } from 'src/composables/useSpotlightSearch';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';

import { serializeActivityListDateRouteParam } from 'src/router/activityListDateRouteParam';

import { EventBus } from 'src/util/eventBus/EventBus';
import { canUseSpotlightSearch, isRunningInNativeCapacitorApp } from 'src/util/platform/browserInteractionCapabilities';
import {
	SHORTCUT_EVENT_CHEATSHEET_TOGGLE,
	SHORTCUT_EVENT_CREATE_APPOINTMENT,
	SHORTCUT_EVENT_CREATE_MAIL,
	SHORTCUT_EVENT_CREATE_NOTE,
	SHORTCUT_EVENT_CREATE_PHONECALL,
	SHORTCUT_EVENT_CREATE_TASK,
	SHORTCUT_EVENT_SEARCH_FIELD_FOCUS,
	SHORTCUT_EVENT_SIDEBAR_TOGGLE,
} from 'src/util/shortcuts/shortcutEvents';

interface BootShortcutsOptions {
	eventBus: EventBus;
	router: Router;
}

export function bootShortcuts(options: BootShortcutsOptions): void {
	if (isRunningInNativeCapacitorApp()) {
		return;
	}

	const { eventBus, router } = options;
	const registry = useShortcutRegistry();

	const safeNavigate = (routeName: string, params?: Record<string, string>): void => {
		void router
			.push({
				name: routeName,
				...(params !== undefined ? { params } : {}),
			})
			.catch(() => {
				/* ignore navigation aborts */
			});
	};

	const navigateToSettings = (): void => {
		safeNavigate('settings', { selectedSettingsTab: 'home' });
	};

	const formatDateYmd = (date: Date): string => date.toISOString().split('T')[0] ?? '';

	const navigateToActivityList = (routeName: string): void => {
		const genericActivityListStore = useGenericActivityListStore();
		const view = String(genericActivityListStore.getActiveView(routeName) || 'open');
		safeNavigate(routeName, {
			date: serializeActivityListDateRouteParam(formatDateYmd(new Date())),
			source: 'main',
			view,
		});
	};

	registry.registerHandlers([
		{
			actionId: 'app.spotlight.toggle',
			handler: () => {
				const userInterfaceSettingsStore = useUserInterfaceSettingsStore();

				if (!canUseSpotlightSearch(userInterfaceSettingsStore.spotlightSearchEnabled)) {
					// Spotlight deaktiviert: stattdessen das Suchfeld im Header fokussieren.
					eventBus.emit(SHORTCUT_EVENT_SEARCH_FIELD_FOCUS);
					return;
				}

				const spotlight = useSpotlightSearch();
				const currentRouteName = router.currentRoute.value.name?.toString() ?? '';
				const defaultModule = MODULE_MAP.includes(currentRouteName) ? currentRouteName : null;
				spotlight.toggle(defaultModule);
			},
		},
		{
			actionId: 'app.settings.open',
			handler: () => {
				navigateToSettings();
			},
		},
		{
			actionId: 'app.cheatsheet.toggle',
			handler: () => {
				eventBus.emit(SHORTCUT_EVENT_CHEATSHEET_TOGGLE);
			},
		},
		{
			actionId: 'app.darkmode.toggle',
			handler: () => {
				const store = useUserInterfaceSettingsStore();
				const nextMode = store.themeMode === 'dark' ? 'light' : 'dark';
				store.setThemeMode(nextMode);
				applyDarkMode(nextMode);
			},
		},
		{
			actionId: 'app.sidebar.toggle',
			handler: () => {
				eventBus.emit(SHORTCUT_EVENT_SIDEBAR_TOGGLE);
			},
		},
		{
			actionId: 'app.create.note',
			handler: () => {
				eventBus.emit(SHORTCUT_EVENT_CREATE_NOTE);
			},
		},
		{
			actionId: 'app.create.task',
			handler: () => {
				eventBus.emit(SHORTCUT_EVENT_CREATE_TASK);
			},
		},
		{
			actionId: 'app.create.mail',
			handler: () => {
				eventBus.emit(SHORTCUT_EVENT_CREATE_MAIL);
			},
		},
		{
			actionId: 'app.create.phonecall',
			handler: () => {
				eventBus.emit(SHORTCUT_EVENT_CREATE_PHONECALL);
			},
		},
		{
			actionId: 'app.create.appointment',
			handler: () => {
				eventBus.emit(SHORTCUT_EVENT_CREATE_APPOINTMENT);
			},
		},
		{
			actionId: 'nav.inbox',
			handler: () => safeNavigate('inbox'),
		},
		{
			actionId: 'nav.tasks',
			handler: () => navigateToActivityList('taskList'),
		},
		{
			actionId: 'nav.mails',
			handler: () => navigateToActivityList('mailList'),
		},
		{
			actionId: 'nav.notes',
			handler: () => navigateToActivityList('noteList'),
		},
		{
			actionId: 'nav.calendar',
			handler: () => safeNavigate('agendaList'),
		},
		{
			actionId: 'nav.phonecalls',
			handler: () => navigateToActivityList('phoneCallList'),
		},
		{
			actionId: 'nav.documents',
			handler: () => navigateToActivityList('documentList'),
		},
		{
			actionId: 'nav.messages',
			handler: () => navigateToActivityList('messageList'),
		},
		{
			actionId: 'nav.kanban',
			handler: () => safeNavigate('kanbanBoard'),
		},
		{
			actionId: 'nav.settings',
			handler: () => navigateToSettings(),
		},
	]);

	attachShortcutListener();
}
